<script setup lang="ts">
// icons
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';

import translationFixtures from '@/locales/fixtures';

const TOP_ACTIVITY_COUNT = 3;

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiCampsiteDetailRatingsProps {
  activities: any;
  suggestedGuests: any;
  hasAds?: boolean;
}
const props = withDefaults(defineProps<CiCampsiteDetailRatingsProps>(), {
  hasAds: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext, $ngettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const maxActivityRecommendations = ref(11);

const hasActivities = computed(() => {
  return props.activities?.length;
});

const hasSuggestions = computed(() => {
  return filteredSuggestions.value?.length;
});

const filteredSuggestions = computed(() => {
  return props.suggestedGuests.filter(suggestion => suggestion.value !== null);
});

const topActivities = computed(() => {
  return props.activities ? props.activities.slice(0, TOP_ACTIVITY_COUNT) : [];
});

const i18nRatedByGuestHeadline = computed(() => {
  return $gettext('<strong>Rated by guests</strong> as "Suitable for"', true);
});

/********************
 * FUNCTIONS         *
 ********************/
function getActivityIcon(activity) {
  let icon = '';
  switch (activity.name.toLowerCase()) {
    case 'diving':
      icon = 'diving';
      break;
    case 'hiking':
      icon = 'hiking';
      break;
    case 'swimming':
      icon = 'swimmer';
      break;
    case 'relaxing':
      icon = 'relaxing';
      break;
    case 'cycling':
      icon = 'cycling';
      break;
    case 'tennis':
      icon = 'tennis';
      break;
    case 'fishing':
      icon = 'fishing';
      break;
    case 'kayak':
      icon = 'kayak';
      break;
    case 'sailing':
      icon = 'seiling';
      break;
    case 'mountainbiking':
      icon = 'mountain_biking';
      break;
    case 'motorcycle':
      icon = 'motor';
      break;
    case 'surfing':
      icon = 'surfing';
      break;
    case 'sightseeing':
      icon = 'sightseeing';
      break;
    case 'ski':
      icon = 'ski';
      break;
    case 'wellness':
      icon = 'wellnes';
      break;
    case 'crosscountry':
      icon = 'hiking';
      break;
    case 'culture':
      icon = 'culture';
      break;
    case 'horsebackriding':
      icon = 'horse';
      break;
    case 'paragliding':
      icon = 'paragliding';
      break;
    case 'party':
      icon = 'party';
      break;
    case 'strolling':
      icon = 'strolling';
      break;
    case 'golf':
      icon = 'golf';
      break;
    case 'culinary':
      icon = 'culinary';
      break;
    case 'motorboat':
      icon = 'motorboat';
      break;
    case 'climbing':
      icon = 'climbing';
      break;
    default:
      icon = false;
      break;
  }
  return icon;
}

function getActivityName(activity) {
  return $gettext(translationFixtures[activity.name.toLowerCase()]);
}

function getOtherActivities(isExpanded) {
  if (isExpanded) {
    return props.activities?.slice(TOP_ACTIVITY_COUNT) || [];
  }

  const maxActivities
    = props.activities?.length < maxActivityRecommendations.value
      ? props.activities.length
      : maxActivityRecommendations.value;
  return props.activities?.slice(TOP_ACTIVITY_COUNT, maxActivities) || [];
}
</script>

<template>
  <div
    v-if="hasActivities || hasSuggestions"
    :class="{ 'mt-12': !props.hasAds }"
  >
    <!-- activities -->
    <template v-if="hasActivities">
      <CiGenericScrollExpand v-slot="{ isExpanded, toggleExpand }">
        <div class="container pt-12">
          <h3 class="mb-4 text-center" v-html="$gettext('<strong>Activities recommended</strong> by guests', true)" />

          <!-- top activities -->
          <template v-if="topActivities.length">
            <div class="flex w-full flex-wrap justify-around gap-[30px]">
              <template
                v-for="(activity, index) in topActivities"
                :key="`top-activities-${index}`"
              >
                <div class="flex flex-col items-center">
                  <img
                    loading="lazy"
                    :src="`/assets/img/activities/icon_${getActivityIcon(activity)}.svg`"
                    class="mx-auto"
                    :alt="activity?.name"
                    width="80"
                    height="80"
                    style="width: 80px; max-width: 100%"
                  />
                  <span class="mt-2">{{ getActivityName(activity) }}</span>
                  <span class="text-small-book text-gray">
                    ({{ activity.ratings_activity }} {{ $ngettext('Visitor', 'Visitors', activity.ratings_activity) }})
                  </span>
                </div>
              </template>
            </div>
            <hr class="my-6 h-px border-0 bg-gray-40" />
          </template>
          <!-- other activities -->
          <div class="grid grid-cols-4 gap-x-[30px]">
            <div
              v-for="(activity, index) in getOtherActivities(isExpanded)"
              :key="`other-activity-${index}`"
              class="col-span-2 mb-6 md:col-span-1"
            >
              <div class="flex items-start justify-start">
                <img
                  v-if="getActivityIcon(activity)"
                  loading="lazy"
                  :src="`/assets/img/activities/icon_${getActivityIcon(activity)}.svg`"
                  :alt="activity?.name"
                  width="30"
                  height="30"
                  style="width: 30px; max-width: 100%"
                />
                <div class="ml-2 flex flex-col overflow-hidden">
                  <div
                    class="line-clamp-2 break-all"
                    :title="getActivityName(activity)"
                  >
                    {{ getActivityName(activity) }}
                  </div>
                  <span class="text-small-book text-gray">({{ activity.ratings_activity }}
                    <span>{{ $ngettext('Visitor', 'Visitors', activity.ratings_activity) }}</span>)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="activities.length > maxActivityRecommendations"
          class="text-center"
        >
          <CiButton
            tag="button"
            variant="text"
            @click="toggleExpand"
          >
            <span>{{
              isExpanded ? $gettext('Show less recommendations') : $gettext('Show more recommendations')
            }}</span>
            <CiAwesomeIcon
              class="ml-2 fill-dark-green"
              :icon="isExpanded ? faChevronUp : faChevronDown"
              ratio="0.5"
            />
          </CiButton>
        </div>
      </CiGenericScrollExpand>
    </template>

    <!-- suggested for -->
    <template v-if="hasSuggestions">
      <div class="container mt-12 pt-12">
        <h3
          class="text-center font-normal"
          v-html="i18nRatedByGuestHeadline"
        />

        <div class="mt-4 grid grid-cols-12 sm:gap-x-[30px]">
          <div
            v-for="(guestRating, index) in filteredSuggestions"
            :key="index"
            class="col-span-12 mb-6 sm:col-span-6 lg:col-span-3"
          >
            <div class="flex h-full items-center overflow-hidden">
              <div class="shrink-0">
                <CiRatingRadial
                  :rating="parseFloat(guestRating.value)"
                  font-class="font-medium"
                  style="width: 48px; max-width: 100%"
                />
              </div>
              <div
                class="ml-2"
                :title="guestRating.title"
              >
                {{ guestRating.title }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
